import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Card } from "@components/Card";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { GoBackButton } from "@components/donate/DonateV3/PaymentProcess/components/GoBackButton";
import {
  PageContainer,
  ProcessContainer,
} from "@components/donate/DonateV3/PaymentProcess/components/PageContainer";
import {
  PaymentProcessRouteName,
  paymentProcessRouteNameToPathMap,
} from "@components/donate/DonateV3/PaymentProcess/components/PaymentProcessLink";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import {
  CreateOrUpdateDonationResult,
  DonateFormType,
} from "@components/donate/DonateV3/types";
import styled from "@emotion/styled";
import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { DonationFlowPaymentOption } from "@every.org/common/src/entity/types";

import { Link } from "src/components/Link";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { LinkAppearance } from "src/styles/link";
import { colorCssVars } from "src/theme/color";
import { verticalStackCss } from "src/theme/spacing";
import { FontWeight } from "src/theme/text";
import { displayCurrencyValueInUserLocale } from "src/utility/currency";
import { logger } from "src/utility/logger";

const CHECK_SUPPORT_URL =
  "https://support.every.org/hc/en-us/articles/1500010677462";

const StyledCard = styled(Card)`
  ${verticalStackCss.l};
  width: 100%;
  border: 2px solid var(${colorCssVars.accent.large});
  strong {
    font-weight: ${FontWeight.BOLD};
  }
`;

interface IraInstructionsProps {
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  nonprofit: ContextNonprofit;
  createOrUpdateDonationResult?: CreateOrUpdateDonationResult;
  handleConfirmedDonation: (result: CreateOrUpdateDonationResult) => boolean;
}

export function IraInstructions({
  form,
  formContext,
  nonprofit,
  createOrUpdateDonationResult,
  handleConfirmedDonation,
}: IraInstructionsProps) {
  const navigate = useNavigate();

  // The pledge was already created on the confirmation step; finish it (updates
  // auth/last-donation state and handles any redirect) and otherwise land on
  // the thank-you page. Mirrors StockInstructions.
  const handleDonationComplete = useCallback(() => {
    if (!createOrUpdateDonationResult) {
      // Shouldn't happen: this page is only reachable after the confirmation
      // step created the pledge. Log it (Sentry) because the donor is looking at
      // check instructions for a pledge we have no record of creating.
      logger.error({
        message: "No created donation on the IRA check instructions page",
        data: { nonprofitId: nonprofit.id },
      });
      return;
    }
    if (!handleConfirmedDonation(createOrUpdateDonationResult)) {
      navigate(
        paymentProcessRouteNameToPathMap[PaymentProcessRouteName.THANK_YOU]
      );
    }
  }, [
    createOrUpdateDonationResult,
    handleConfirmedDonation,
    navigate,
    nonprofit.id,
  ]);

  const currency = form.watch("currency");
  const email = form.watch("email");
  const amountString = displayCurrencyValueInUserLocale({
    currencyValue: {
      currency,
      amount: formContext.totalAmountBig,
    },
  });

  return (
    <PageContainer>
      <GoBackButton
        form={form}
        formContext={formContext}
        paymentOption={DonationFlowPaymentOption.IRA}
      />
      <ProcessContainer css={verticalStackCss.l}>
        <StyledCard>
          <div css={verticalStackCss.xs}>
            <Icon
              size={IconSize.LARGE}
              display={IconDisplay.ACCENT}
              iconImport={() => import("@components/Icon/icons/FileIcon")}
            />
            <h3>Complete your gift with these instructions</h3>
          </div>
          <p>
            You&rsquo;ve pledged to donate <strong>{amountString}</strong>{" "}
            through your IRA.
          </p>
          <p>
            To complete this donation, ask your IRA provider how to make a
            &quot;qualified charitable distribution (QCD)&quot; via check with
            the following details:
          </p>
          <p>
            <strong>Payable to:</strong> Every.org
            <br />
            <strong>Memo:</strong> {nonprofit.primarySlug}
            <span className="rr-block">
              {email ? `, ${email} (optional)` : ""}
            </span>
          </p>
          <p>Then mail the check to:</p>
          <p>
            <strong>
              Every.org
              <br />
              58 West Portal Ave, #781
              <br />
              San Francisco, CA 94127
            </strong>
          </p>
          <p>
            <strong>Contact info:</strong> Karissa Sparks, COO
            <br />
            <strong>Phone number:</strong> +1 (415) 650-0503
          </p>
          <div css={verticalStackCss.xs}>
            <p>
              Please make sure the check and envelope are addressed to
              Every.org, not the nonprofit&apos;s name, otherwise we won&apos;t
              be able to process the check.
            </p>
            <Link
              data-tname="ira-check-examples"
              appearance={LinkAppearance.HYPERLINK}
              to={CHECK_SUPPORT_URL}
            >
              See examples of how to fill out the check
            </Link>
          </div>
          <p>These instructions will also be emailed to you</p>
        </StyledCard>
        <Button
          data-tname="donate-ira-instructions"
          data-bloom-block
          css={{ alignSelf: "flex-end" }}
          role={ButtonRole.PRIMARY}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: handleDonationComplete,
          }}
        >
          I will notify my IRA provider
        </Button>
      </ProcessContainer>
    </PageContainer>
  );
}
